import { assessFraming, framingSceneAt, modelStatesAt } from './framing.js';
import { modelScale, modelSlugOf } from './models.js';
import { isSmallModel } from './model-distance.js';
import { isStagedLesson } from './panos.js';
import { STAGE, STAGE_ADR032 } from './stage.js';
import { featureOf } from './world-data.js';
import { CODE_WORLD_CATALOGUE, featuresOf, stageOf, worldIn, } from './world-catalogue.js';
import { currentFeature, currentState } from './world-package/order.js';
/**
 * Worlds: real-looking places around a clear stage
 * (docs/superpowers/specs/2026-09-28-worlds-design.md, ADR-032).
 *
 * A world is engine, not lesson content: a lesson names its slug and the
 * headset builds the place. What a lesson must respect is the stage -- a
 * flat circle around the origin where every object and every place the pupil
 * stands must be -- so a lesson never meets the scenery and the lectern
 * never swings into a rock. Pure, so the headset, the API's generator check
 * and `pnpm lessons:check` say the same.
 */
export { FEATURE_PREFIX, WORLDS, featureOf } from './world-data.js';
export { featuresOf } from './world-catalogue.js';
export { STAGE, STAGE_ADR032 };
export function isWorldLesson(lesson) {
    return lesson.scene.type === 'world';
}
const dutch = (n) => n.toFixed(1).replace('.', ',');
/** How far from the origin, horizontally, an object reaches. */
function reach(bounds) {
    if (bounds.ring) {
        return Math.hypot(bounds.ring.centre[0], bounds.ring.centre[1]) + bounds.ring.outer;
    }
    if (bounds.round) {
        return Math.hypot(bounds.round.centre[0], bounds.round.centre[2]) + bounds.round.radius;
    }
    // A move (ADR-102): as far as the hull of its ends and turns, never the corners of the box around them.
    if (bounds.swept) {
        return Math.max(...bounds.swept.ends.map(boxReach), ...bounds.swept.discs.map((disc) => Math.hypot(disc.centre[0], disc.centre[1]) + disc.radius));
    }
    return boxReach(bounds.obstacle);
}
/** How far from the origin, horizontally, a box reaches: at a corner. */
function boxReach({ min, max }) {
    let far = 0;
    for (const x of [min[0], max[0]]) {
        for (const z of [min[2], max[2]])
            far = Math.max(far, Math.hypot(x, z));
    }
    return far;
}
/** Its lowest point. */
function bottom(bounds) {
    return bounds.round ? bounds.round.centre[1] - bounds.round.height : bounds.obstacle.min[1];
}
/**
 * What in a world or 360° photo lesson leaves the stage (`stageOf`, ADR-061):
 * every small object within 6 m (`radius`) at any step, over the whole way a
 * move or a turn carries it (named once, at its farthest), none that sinks
 * into the ground, and every place the pupil stands within the standing area
 * (`stand`, 2 m since ADR-061). Nothing for any other scene: the studio has
 * no stage rule.
 *
 * With a `catalogue` it sees library models (ADR-043): a small one keeps to
 * the stage like a shape; a big one -- an animal, or a static exhibit too
 * long for the stage, such as a life-size skeleton -- is left to the
 * distance rule (model-steps.ts), which says where it may go.
 */
export function assessStage(lesson, catalogue, worlds) {
    if (!isStagedLesson(lesson, worlds))
        return [];
    const stage = stageOf(lesson, worlds);
    const farthest = new Map();
    const sinks = new Set();
    const statesAt = catalogue ? modelStatesAt(lesson, catalogue, undefined, worlds) : undefined;
    const big = new Set(Object.entries(lesson.objects ?? {})
        .filter(([, object]) => {
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : catalogue?.[slug];
        return facts !== undefined && !isSmallModel(facts, modelScale(object, facts));
    })
        .map(([id]) => id));
    lesson.steps.forEach((_step, index) => {
        const scene = framingSceneAt(lesson, index, statesAt, catalogue, worlds);
        for (const [id, bounds] of scene.obstacles ?? scene.bounds) {
            if (big.has(id))
                continue;
            farthest.set(id, Math.max(farthest.get(id) ?? 0, reach(bounds)));
            if (bottom(bounds) < -stage.below - 1e-9)
                sinks.add(id);
        }
    });
    const problems = [];
    for (const [id, distance] of farthest) {
        if (distance <= stage.radius + 1e-6)
            continue;
        problems.push({
            kind: 'outside',
            object: id,
            distance,
            radius: stage.radius,
            detail: `"${id}" komt te ver van het midden (${dutch(distance)} m; hooguit ${stage.radius})`,
        });
    }
    for (const id of sinks) {
        problems.push({ kind: 'below', object: id, detail: `"${id}" zakt in de grond` });
    }
    const framing = assessFraming(lesson, 1.6, { ...(statesAt ? { statesAt } : {}), ...(worlds ? { worlds } : {}) }, catalogue);
    for (const step of framing.steps) {
        const distance = Math.hypot(step.eye[0], step.eye[2]);
        if (distance <= stage.stand + 1e-6)
            continue;
        problems.push({
            kind: 'pupil',
            step: step.step,
            detail: `stap "${step.step}": de leerling staat buiten het podium (${dutch(distance)} m van het midden; hooguit ${String(stage.stand).replace('.', ',')})`,
        });
    }
    return problems;
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * Whether a lesson's `wereld:` references, hotspots on a feature (ADR-087) and
 * step states fit its world: a feature the world has, a state the feature has,
 * and none outside a world. Reads the raw document, so an answer the schema refused still says
 * which step broke what ("Wereld klopt").
 */
export function assessWorldFeatures(document, worlds) {
    const problems = [];
    if (!isRecord(document))
        return problems;
    const scene = isRecord(document['scene']) ? document['scene'] : {};
    const inWorld = scene['type'] === 'world' && typeof scene['world'] === 'string';
    const world = inWorld ? scene['world'] : '';
    const features = inWorld ? featuresOf({ scene }, worlds) : {};
    // An old name the world keeps (order.ts) is read as the current one.
    const aliases = inWorld ? aliasesOf(world, worlds) : undefined;
    // Features are looked up as own entries: a step may say "constructor" and mean nothing.
    const currentOf = (name) => currentFeature(features, aliases, name);
    const featureNamed = (name) => {
        const current = currentOf(name);
        return current === undefined ? undefined : features[current];
    };
    const names = Object.keys(features);
    const steps = Array.isArray(document['steps']) ? document['steps'] : [];
    steps.forEach((raw, index) => {
        if (!isRecord(raw))
            return;
        const at = `/steps/${index}`;
        const said = `stap "${typeof raw['id'] === 'string' ? raw['id'] : index + 1}": `;
        const view = isRecord(raw['view']) ? raw['view'] : {};
        const named = new Set();
        for (const [path, reference] of [
            [`${at}/view/look`, view['look']],
            [`${at}/focus`, raw['focus']],
        ]) {
            const feature = featureOf(reference);
            // A step that looks at and is about the same feature is told once.
            if (feature === undefined || named.has(reference))
                continue;
            named.add(reference);
            if (!inWorld) {
                problems.push({
                    path,
                    en: `"${String(reference)}" names a world feature, which only a world lesson has`,
                    nl: `${said}"${String(reference)}" kan alleen in een wereld`,
                });
            }
            else if (!featureNamed(feature)) {
                problems.push({
                    path,
                    en: `"${String(reference)}" is not a feature of ${world}`,
                    nl: `${said}de wereld heeft geen "${feature}" (wel: ${names.join(', ')})`,
                });
            }
        }
        // A hotspot on a feature (ADR-087): any state of a feature the world has, by any name it keeps.
        const hotspots = Array.isArray(raw['hotspots']) ? raw['hotspots'] : [];
        hotspots.forEach((hotspot, h) => {
            if (!isRecord(hotspot))
                return;
            const feature = hotspot['feature'];
            if (typeof feature !== 'string')
                return;
            const path = `${at}/hotspots/${h}/feature`;
            if (!inWorld) {
                problems.push({
                    path,
                    en: `a hotspot on feature "${feature}" goes only in a world lesson`,
                    nl: `${said}hotspot ${h + 1} op "${feature}" kan alleen in een wereld`,
                });
            }
            else if (!featureNamed(feature)) {
                problems.push({
                    path,
                    en: `"${feature}" is not a feature of ${world}`,
                    nl: `${said}de wereld heeft geen "${feature}" (wel: ${names.join(', ')})`,
                });
            }
        });
        const states = raw['world'];
        if (states === undefined)
            return;
        if (!inWorld) {
            problems.push({
                path: `${at}/world`,
                en: 'a step sets world states, which only a world lesson has',
                nl: `${said}"world" kan alleen in een wereld`,
            });
            return;
        }
        if (!isRecord(states))
            return;
        for (const [feature, state] of Object.entries(states)) {
            const known = featureNamed(feature);
            const current = currentOf(feature);
            if (!known || current === undefined) {
                problems.push({
                    path: `${at}/world/${feature}`,
                    en: `"${feature}" is not a feature of ${world}`,
                    nl: `${said}de wereld heeft geen "${feature}" (wel: ${names.join(', ')})`,
                });
            }
            else if (typeof state !== 'string' ||
                currentState(known.states, aliases, current, state) === undefined) {
                problems.push({
                    path: `${at}/world/${feature}`,
                    en: `"${feature}" has no state "${String(state)}"; use one of ${known.states.join(', ')}`,
                    nl: `${said}"${feature}" kan niet "${String(state)}" zijn (${known.states.join(', ')})`,
                });
            }
        }
    });
    return problems;
}
/**
 * Each feature's state at step `index`: the last one a step set at or before
 * it, else its initial one. From the index alone, like `sceneStateAt`, so a
 * late joiner and a jump see the same world. -1 is before the first step.
 */
export function worldStateAt(lesson, index, worlds) {
    const states = new Map();
    const features = featuresOf(lesson, worlds);
    for (const [name, feature] of Object.entries(features))
        states.set(name, feature.initial);
    const aliases = lesson.scene.type === 'world' ? aliasesOf(lesson.scene.world, worlds) : undefined;
    lesson.steps.slice(0, Math.max(0, index + 1)).forEach((step) => {
        for (const [name, state] of Object.entries(step.world ?? {})) {
            // An old name the world keeps (order.ts) sets the current feature to the current state.
            const current = currentFeature(features, aliases, name);
            if (current === undefined)
                continue;
            states.set(current, currentState(features[current].states, aliases, current, state) ?? state);
        }
    });
    return states;
}
/** The old names of the world with this slug (order.ts), when it keeps any. */
function aliasesOf(slug, worlds) {
    return worldIn(worlds ?? CODE_WORLD_CATALOGUE, slug)?.aliases;
}
/**
 * The lesson with every old name of its world (a package's `eerdereOnderdelen` and `eerder`; order.ts)
 * written as the current one: a step's `world`, `view.look`, `focus` and a hotspot's `feature` (ADR-087).
 * The same lesson when its world
 * keeps no old names. The headset plays a lesson so, after it has validated it; the stored lesson
 * keeps its own words.
 */
export function withCurrentWorldNames(lesson, worlds) {
    if (lesson.scene.type !== 'world')
        return lesson;
    const aliases = aliasesOf(lesson.scene.world, worlds);
    if (aliases === undefined)
        return lesson;
    const features = featuresOf(lesson, worlds);
    const reference = (value) => {
        const name = featureOf(value);
        if (name === undefined || value === undefined)
            return value;
        const current = currentFeature(features, aliases, name);
        return current === undefined
            ? value
            : `${value.slice(0, value.length - name.length)}${current}`;
    };
    return {
        ...lesson,
        steps: lesson.steps.map((step) => {
            let out = step;
            if (step.world !== undefined) {
                const world = {};
                for (const [name, state] of Object.entries(step.world)) {
                    const current = currentFeature(features, aliases, name);
                    if (current === undefined) {
                        world[name] = state;
                        continue;
                    }
                    world[current] =
                        currentState(features[current].states, aliases, current, state) ?? state;
                }
                out = { ...out, world };
            }
            if (typeof step.focus === 'string' && featureOf(step.focus) !== undefined)
                out = { ...out, focus: reference(step.focus) };
            const view = step.view;
            if (view !== undefined && 'look' in view && typeof view.look === 'string')
                if (featureOf(view.look) !== undefined)
                    out = { ...out, view: { ...view, look: reference(view.look) } };
            if (step.hotspots?.some((hotspot) => hotspot.feature !== undefined))
                out = {
                    ...out,
                    hotspots: step.hotspots.map((hotspot) => hotspot.feature === undefined
                        ? hotspot
                        : {
                            ...hotspot,
                            feature: currentFeature(features, aliases, hotspot.feature) ?? hotspot.feature,
                        }),
                };
            return out;
        }),
    };
}
//# sourceMappingURL=worlds.js.map